import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { Box, Card, CardActionArea, Radio, RadioGroup, Typography } from '@mui/material';
import React from 'react';
/**
 * A component that displays a list of selectable icons as radio buttons.
 * Each option is presented as a card with an icon, label, and corresponding radio input.
 *
 * @component
 *
 * @param {RadioIconPickerProps} props - Props for configuring the radio icon picker.
 * @param {RadioIcon[]} [props.items] - Array of radio icon options to display.
 * @param {string} [props.selectedItemValue] - The `value` of the currently selected item.
 * @param {(item?: RadioIcon) => void} props.onSelectItem - Callback when an item is selected.
 * @param {string} [props.'data-test'] - Test ID for the root component.
 * @param {string} [props.ariaDescribedBy] - ARIA `aria-describedby` attribute for accessibility.
 *
 * @returns {React.ReactElement} A group of cards with radio buttons that function as a picker.
 */
export const RadioIconPicker = (props) => {
    var _a, _b;
    const uncheckedIconSx = { border: '1px solid', borderColor: themeColors.border.main, padding: '1px' };
    const checkedIconSx = { border: '2px solid', borderColor: themeColors.primary.base };
    const paddingXAndBottom = { px: 2, pb: 2 };
    return (React.createElement(RadioGroup, { row: true, sx: { gap: 2 }, "data-test": (_a = props['data-test']) !== null && _a !== void 0 ? _a : 'radio-icon-picker' }, (_b = props.items) === null || _b === void 0 ? void 0 : _b.map((item) => {
        var _a, _b;
        return (React.createElement(Card, { variant: 'outlined', key: item.value, sx: item.value === props.selectedItemValue ? checkedIconSx : uncheckedIconSx, onClick: () => props.onSelectItem(item) },
            React.createElement(CardActionArea, { "aria-label": (_a = item['aria-label']) !== null && _a !== void 0 ? _a : item.label, "aria-describedby": props.ariaDescribedBy, "data-test": (_b = item['data-test']) !== null && _b !== void 0 ? _b : 'card' },
                React.createElement(Box, { sx: { display: 'flex', justifyContent: 'flex-end' }, role: 'none' },
                    React.createElement(Radio, { sx: {
                            pt: 1,
                            pr: 1,
                            '&.Mui-disabled': { color: themeColors.primary.base },
                        }, size: 'small', value: item.value, checked: item.value === props.selectedItemValue, disableRipple: true, "aria-hidden": true, inputProps: { 'aria-hidden': true }, disabled: true, "data-test": 'radio-button' })),
                React.createElement(Box, { sx: paddingXAndBottom, "data-test": 'image' }, item.element),
                React.createElement(Box, { sx: Object.assign(Object.assign({}, paddingXAndBottom), { display: 'flex', justifyContent: 'center' }), "data-test": 'label' },
                    React.createElement(Typography, { variant: 'subtitle2', sx: { color: themeColors.primary.base } }, item.label)))));
    })));
};
